import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import type { Me } from "@opsbelt/contract";

import { AppLauncher } from "@/components/AppLauncher";
import { Sidebar } from "@/components/Sidebar";
import { OpsBeltMark } from "@/components/OpsBeltMark";
import { api } from "@/lib/api";
import { signOut, useSession } from "@/lib/auth";

/** Pages read the active org/user via useOutletContext<MeContext>(). */
export type MeContext = { me: Me | undefined };

export function AppShell() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    enabled: !!session,
  });

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <OpsBeltMark size={36} className="animate-pulse text-primary" />
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  const ctx: MeContext = { me: meQuery.data };

  return (
    <div className="flex h-full">
      <Sidebar me={meQuery.data} onSignOut={handleSignOut} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-[60px] shrink-0 items-center justify-end gap-2 border-b border-border px-6">
          <AppLauncher />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1080px] px-8 py-8">
            <Outlet context={ctx} />
          </div>
        </main>
      </div>
    </div>
  );
}
